import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import {
  SectorBridge,
  SectorBridgeGrid,
  SectorBridgeRequestPayload,
} from '@/features/sectorBridges/types';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const sectorBridgesApi = createApi({
  reducerPath: 'sectorBridgesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/sector-bridges`,
  }),
  tagTypes: ['SectorBridges', 'SectorBridge'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getSectorBridgesI: builder.infiniteQuery<
      SectorBridgeGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['SectorBridges', 'SectorBridge'],
    }),
    getSectorBridgeById: builder.query<SectorBridge, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: 'SectorBridge', id }],
    }),
    createSectorBridge: builder.mutation<
      SectorBridge,
      SectorBridgeRequestPayload
    >({
      query: data => ({
        url: ``,
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'SectorBridges',
        { type: 'SectorBridge', id: result?.id },
      ],
    }),
    updateSectorBridgeById: builder.mutation<
      SectorBridge,
      SectorBridgeRequestPayload
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'SectorBridges',
        { type: 'SectorBridge', id: result?.id },
      ],
    }),
    deleteSectorBridgeById: builder.mutation<SectorBridge, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'SectorBridges',
        { type: 'SectorBridge', id },
      ],
    }),
  }),
});

export const {
  useGetSectorBridgeByIdQuery,
  useLazyGetSectorBridgeByIdQuery,
  useCreateSectorBridgeMutation,
  useUpdateSectorBridgeByIdMutation,
  useDeleteSectorBridgeByIdMutation,
} = sectorBridgesApi;

export const useGetSectorBridgesIInfiniteQuery = normalizeInfiniteQueryData(
  sectorBridgesApi.useGetSectorBridgesIInfiniteQuery,
);

const getCachedSectorBridgeByIdSelector = cachedItemByIdSelector(
  'sectorBridgesApi',
  'getSectorBridgesI',
);

export const useGetCachedSectorBridgeByIdSelector = id =>
  useSelector(getCachedSectorBridgeByIdSelector(id));
